import type {
  MeasurementUnit,
  RackRotation,
  Warehouse,
  WarehouseZone,
  WarehouseRack,
  WarehouseBin,
  WarehouseStatistics,
  WarehouseCreateData,
  ZoneCreateData,
  RackCreateData,
  ValidationError,
  WarehouseValidationResult,
  BoundingBox,
  Position,
  UnitConversionResult,
} from '../types/warehouse';

export { ZONE_COLORS } from '../types/warehouse';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function getBayLetter(index: number): string {
  if (index < 0) return 'A';
  if (index < 26) return ALPHABET[index];

  const firstLetter = ALPHABET[Math.floor(index / 26) - 1];
  const secondLetter = ALPHABET[index % 26];
  return `${firstLetter}${secondLetter}`;
}

export function getBayIndex(letter: string): number {
  if (!letter || letter.length === 0) return 0;

  const upperLetter = letter.toUpperCase();

  if (upperLetter.length === 1) {
    return ALPHABET.indexOf(upperLetter);
  }

  const firstIndex = ALPHABET.indexOf(upperLetter[0]) + 1;
  const secondIndex = ALPHABET.indexOf(upperLetter[1]);
  return firstIndex * 26 + secondIndex;
}

export function formatZoneCode(zoneNumber: number): string {
  return `ZONE-${String(zoneNumber).padStart(2, '0')}`;
}

export function formatRackCode(rackNumber: number): string {
  return `RACK-${String(rackNumber).padStart(2, '0')}`;
}

export function formatBinCode(rackCode: string, bayLetter: string, level: number): string {
  return `${rackCode}-${bayLetter}${level}`;
}

export function generateBinsForRack(
  rackId: string,
  warehouseId: string,
  adminId: string,
  rackCode: string,
  levels: number,
  bays: number
): Omit<WarehouseBin, 'id' | 'created_at'>[] {
  const bins: Omit<WarehouseBin, 'id' | 'created_at'>[] = [];
  let sortOrder = 0;

  for (let bayIndex = 0; bayIndex < bays; bayIndex++) {
    const bayLetter = getBayLetter(bayIndex);

    for (let level = 1; level <= levels; level++) {
      bins.push({
        rack_id: rackId,
        warehouse_id: warehouseId,
        admin_id: adminId,
        bin_code: formatBinCode(rackCode, bayLetter, level),
        bay_letter: bayLetter,
        bay_index: bayIndex + 1,
        level_number: level,
        sort_order: sortOrder++,
      });
    }
  }

  return bins;
}

export function convertUnit(
  value: number,
  fromUnit: MeasurementUnit,
  toUnit: MeasurementUnit
): number {
  if (fromUnit === toUnit) return value;

  if (fromUnit === 'm' && toUnit === 'cm') {
    return value * 100;
  }

  if (fromUnit === 'cm' && toUnit === 'm') {
    return value / 100;
  }

  return value;
}

export function formatDimension(value: number, unit: MeasurementUnit): string {
  const formatted = unit === 'm'
    ? value.toFixed(2)
    : Math.round(value).toString();
  return `${formatted} ${unit}`;
}

export function formatDimensions(
  length: number,
  width: number,
  unit: MeasurementUnit
): string {
  return `${formatDimension(length, unit)} x ${formatDimension(width, unit)}`;
}

export function getUnitDisplayInfo(unit: MeasurementUnit): UnitConversionResult {
  return {
    value: unit === 'm' ? 1 : 100,
    unit,
    displayValue: unit === 'm' ? 'meters' : 'centimeters',
  };
}

export function calculateWarehouseStatistics(
  zones: WarehouseZone[],
  racks: WarehouseRack[],
  bins: WarehouseBin[]
): WarehouseStatistics {
  const racksInZones = racks.filter(r => r.zone_id !== null).length;
  const racksUnassigned = racks.filter(r => r.zone_id === null).length;

  const utilizationPercentage = racks.length > 0
    ? Math.round((racksInZones / racks.length) * 100)
    : 0;

  return {
    total_zones: zones.length,
    total_racks: racks.length,
    total_bins: bins.length,
    racks_in_zones: racksInZones,
    racks_unassigned: racksUnassigned,
    utilization_percentage: utilizationPercentage,
  };
}

export function getNextAvailableZoneNumber(existingZones: WarehouseZone[]): number {
  if (existingZones.length === 0) return 1;

  const numbers = existingZones.map(z => z.zone_number).sort((a, b) => a - b);

  for (let i = 1; i <= numbers.length + 1; i++) {
    if (!numbers.includes(i)) return i;
  }

  return numbers[numbers.length - 1] + 1;
}

export function getNextAvailableRackNumber(existingRacks: WarehouseRack[]): number {
  if (existingRacks.length === 0) return 1;

  const numbers = existingRacks.map(r => r.rack_number).sort((a, b) => a - b);

  for (let i = 1; i <= numbers.length + 1; i++) {
    if (!numbers.includes(i)) return i;
  }

  return numbers[numbers.length - 1] + 1;
}

export function getNextAvailableZoneColor(
  existingZones: WarehouseZone[],
  colors: readonly string[] = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
    '#14B8A6', '#A855F7'
  ]
): string {
  const usedColors = new Set(existingZones.map(z => z.color.toUpperCase()));

  for (const color of colors) {
    if (!usedColors.has(color.toUpperCase())) {
      return color;
    }
  }

  return colors[existingZones.length % colors.length];
}

export function getRackBoundingBox(rack: WarehouseRack): BoundingBox {
  const isRotated = rack.rotation === 90 || rack.rotation === 270;

  return {
    x: rack.x_position,
    y: rack.y_position,
    width: isRotated ? rack.rack_depth : rack.rack_length,
    height: isRotated ? rack.rack_length : rack.rack_depth,
  };
}

export function getZoneBoundingBox(zone: WarehouseZone): BoundingBox {
  return {
    x: zone.x_position,
    y: zone.y_position,
    width: zone.zone_width,
    height: zone.zone_height,
  };
}

export function isPointInBoundingBox(point: Position, box: BoundingBox): boolean {
  return (
    point.x >= box.x &&
    point.x <= box.x + box.width &&
    point.y >= box.y &&
    point.y <= box.y + box.height
  );
}

export function doBoxesOverlap(box1: BoundingBox, box2: BoundingBox): boolean {
  return !(
    box1.x + box1.width <= box2.x ||
    box2.x + box2.width <= box1.x ||
    box1.y + box1.height <= box2.y ||
    box2.y + box2.height <= box1.y
  );
}

export function getRackCenterPoint(rack: WarehouseRack): Position {
  const box = getRackBoundingBox(rack);
  return {
    x: box.x + box.width / 2,
    y: box.y + box.height / 2,
  };
}

export function getZoneCenterPoint(zone: WarehouseZone): Position {
  return {
    x: zone.x_position + zone.zone_width / 2,
    y: zone.y_position + zone.zone_height / 2,
  };
}

export function calculateOverlapArea(box1: BoundingBox, box2: BoundingBox): number {
  const xOverlap = Math.max(
    0,
    Math.min(box1.x + box1.width, box2.x + box2.width) - Math.max(box1.x, box2.x)
  );
  const yOverlap = Math.max(
    0,
    Math.min(box1.y + box1.height, box2.y + box2.height) - Math.max(box1.y, box2.y)
  );
  return xOverlap * yOverlap;
}

export function findBestZoneForRack(
  rack: WarehouseRack,
  zones: WarehouseZone[]
): WarehouseZone | null {
  if (zones.length === 0) return null;

  const rackBox = getRackBoundingBox(rack);
  const rackArea = rackBox.width * rackBox.height;

  let bestZone: WarehouseZone | null = null;
  let bestOverlapRatio = 0;

  for (const zone of zones) {
    const zoneBox = getZoneBoundingBox(zone);
    const overlapArea = calculateOverlapArea(rackBox, zoneBox);
    const overlapRatio = overlapArea / rackArea;

    if (overlapRatio > bestOverlapRatio) {
      bestOverlapRatio = overlapRatio;
      bestZone = zone;
    }
  }

  return bestOverlapRatio >= 0.5 ? bestZone : null;
}

export function autoAssignRacksToZones(
  racks: WarehouseRack[],
  zones: WarehouseZone[]
): Map<string, string | null> {
  const assignments = new Map<string, string | null>();

  for (const rack of racks) {
    if (rack.zone_manual_override && rack.zone_id) {
      assignments.set(rack.id, rack.zone_id);
      continue;
    }

    const bestZone = findBestZoneForRack(rack, zones);
    assignments.set(rack.id, bestZone?.id ?? null);
  }

  return assignments;
}

export function snapToGrid(value: number, gridSize: number): number {
  return Math.round(value / gridSize) * gridSize;
}

export function snapPositionToGrid(position: Position, gridSize: number): Position {
  return {
    x: snapToGrid(position.x, gridSize),
    y: snapToGrid(position.y, gridSize),
  };
}

export function clampToWarehouseBounds(
  position: Position,
  elementSize: { width: number; height: number },
  warehouseSize: { length: number; width: number },
  unit: MeasurementUnit,
  scale: number = 1
): Position {
  const warehousePixelWidth = convertToPixels(warehouseSize.length, unit, scale);
  const warehousePixelHeight = convertToPixels(warehouseSize.width, unit, scale);

  return {
    x: Math.max(0, Math.min(position.x, warehousePixelWidth - elementSize.width)),
    y: Math.max(0, Math.min(position.y, warehousePixelHeight - elementSize.height)),
  };
}

export function convertToPixels(
  value: number,
  unit: MeasurementUnit,
  scale: number = 1
): number {
  const basePixelsPerMeter = 100;
  const valueInMeters = unit === 'cm' ? value / 100 : value;
  return valueInMeters * basePixelsPerMeter * scale;
}

export function convertFromPixels(
  pixels: number,
  unit: MeasurementUnit,
  scale: number = 1
): number {
  const basePixelsPerMeter = 100;
  const valueInMeters = pixels / (basePixelsPerMeter * scale);
  return unit === 'cm' ? valueInMeters * 100 : valueInMeters;
}

export function validateWarehouseData(
  data: WarehouseCreateData,
  existingWarehouses: Warehouse[] = []
): WarehouseValidationResult {
  const errors: ValidationError[] = [];

  if (!data.name || data.name.trim().length === 0) {
    errors.push({ field: 'name', message: 'Warehouse name is required' });
  } else if (data.name.trim().length > 100) {
    errors.push({ field: 'name', message: 'Warehouse name must be 100 characters or less' });
  } else {
    const nameExists = existingWarehouses.some(
      w => w.name.toLowerCase() === data.name.trim().toLowerCase() && !w.is_archived
    );
    if (nameExists) {
      errors.push({ field: 'name', message: 'A warehouse with this name already exists' });
    }
  }

  if (!data.length || data.length <= 0) {
    errors.push({ field: 'length', message: 'Length must be greater than 0' });
  }

  if (!data.width || data.width <= 0) {
    errors.push({ field: 'width', message: 'Width must be greater than 0' });
  }

  if (data.height !== undefined && data.height !== null && data.height <= 0) {
    errors.push({ field: 'height', message: 'Height must be greater than 0 if provided' });
  }

  if (!data.unit || !['m', 'cm'].includes(data.unit)) {
    errors.push({ field: 'unit', message: 'Unit must be either "m" or "cm"' });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function validateZoneData(data: ZoneCreateData): WarehouseValidationResult {
  const errors: ValidationError[] = [];

  if (!data.warehouse_id) {
    errors.push({ field: 'warehouse_id', message: 'Warehouse ID is required' });
  }

  if (!data.color || !/^#[0-9A-Fa-f]{6}$/.test(data.color)) {
    errors.push({ field: 'color', message: 'Valid hex color is required (e.g., #3B82F6)' });
  }

  if (data.zone_width !== undefined && data.zone_width <= 0) {
    errors.push({ field: 'zone_width', message: 'Zone width must be greater than 0' });
  }

  if (data.zone_height !== undefined && data.zone_height <= 0) {
    errors.push({ field: 'zone_height', message: 'Zone height must be greater than 0' });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function validateRackData(data: RackCreateData): WarehouseValidationResult {
  const errors: ValidationError[] = [];

  if (!data.warehouse_id) {
    errors.push({ field: 'warehouse_id', message: 'Warehouse ID is required' });
  }

  if (data.rack_length !== undefined && data.rack_length <= 0) {
    errors.push({ field: 'rack_length', message: 'Rack length must be greater than 0' });
  }

  if (data.rack_depth !== undefined && data.rack_depth <= 0) {
    errors.push({ field: 'rack_depth', message: 'Rack depth must be greater than 0' });
  }

  if (data.levels !== undefined && (data.levels < 1 || !Number.isInteger(data.levels))) {
    errors.push({ field: 'levels', message: 'Levels must be a positive integer' });
  }

  if (data.bays !== undefined && (data.bays < 1 || !Number.isInteger(data.bays))) {
    errors.push({ field: 'bays', message: 'Bays must be a positive integer' });
  }

  if (data.rotation !== undefined && ![0, 90, 180, 270].includes(data.rotation)) {
    errors.push({ field: 'rotation', message: 'Rotation must be 0, 90, 180, or 270' });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function getRotationLabel(rotation: RackRotation): string {
  const labels: Record<RackRotation, string> = {
    0: '0°',
    90: '90°',
    180: '180°',
    270: '270°',
  };
  return labels[rotation];
}

export function getNextRotation(current: RackRotation): RackRotation {
  const rotations: RackRotation[] = [0, 90, 180, 270];
  const currentIndex = rotations.indexOf(current);
  return rotations[(currentIndex + 1) % rotations.length];
}

export function calculateTotalBinCount(racks: WarehouseRack[]): number {
  return racks.reduce((total, rack) => total + rack.levels * rack.bays, 0);
}

export function groupRacksByZone(
  racks: WarehouseRack[],
  zones: WarehouseZone[]
): Map<string | null, WarehouseRack[]> {
  const grouped = new Map<string | null, WarehouseRack[]>();

  grouped.set(null, []);
  for (const zone of zones) {
    grouped.set(zone.id, []);
  }

  for (const rack of racks) {
    const zoneId = rack.zone_id;
    const existing = grouped.get(zoneId) ?? [];
    existing.push(rack);
    grouped.set(zoneId, existing);
  }

  return grouped;
}

export function sortZonesByNumber(zones: WarehouseZone[]): WarehouseZone[] {
  return [...zones].sort((a, b) => a.zone_number - b.zone_number);
}

export function sortRacksByNumber(racks: WarehouseRack[]): WarehouseRack[] {
  return [...racks].sort((a, b) => a.rack_number - b.rack_number);
}

export function sortBinsByPosition(bins: WarehouseBin[]): WarehouseBin[] {
  return [...bins].sort((a, b) => a.sort_order - b.sort_order);
}

export interface AutoLayoutConfig {
  warehouseLength: number;
  warehouseWidth: number;
  padding: number;
  zoneSpacing: number;
  rackSpacing: number;
  aisleWidth: number;
}

export interface ZoneLayoutResult {
  zoneId: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RackLayoutResult {
  rackId: string;
  x: number;
  y: number;
  rotation: RackRotation;
}

const DEFAULT_AUTO_LAYOUT_CONFIG: AutoLayoutConfig = {
  warehouseLength: 100,
  warehouseWidth: 100,
  padding: 5,
  zoneSpacing: 3,
  rackSpacing: 2,
  aisleWidth: 4,
};

export function calculateAutoLayoutForZones(
  zones: WarehouseZone[],
  config: Partial<AutoLayoutConfig> = {}
): ZoneLayoutResult[] {
  const cfg = { ...DEFAULT_AUTO_LAYOUT_CONFIG, ...config };
  const results: ZoneLayoutResult[] = [];

  if (zones.length === 0) return results;

  const availableWidth = cfg.warehouseLength - cfg.padding * 2;
  const availableHeight = cfg.warehouseWidth - cfg.padding * 2;

  const numZones = zones.length;
  const cols = Math.ceil(Math.sqrt(numZones));
  const rows = Math.ceil(numZones / cols);

  const zoneWidth = (availableWidth - (cols - 1) * cfg.zoneSpacing) / cols;
  const zoneHeight = (availableHeight - (rows - 1) * cfg.zoneSpacing) / rows;

  const sortedZones = sortZonesByNumber(zones);

  sortedZones.forEach((zone, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);

    const x = cfg.padding + col * (zoneWidth + cfg.zoneSpacing);
    const y = cfg.padding + row * (zoneHeight + cfg.zoneSpacing);

    results.push({
      zoneId: zone.id,
      x: Math.round(x * 10) / 10,
      y: Math.round(y * 10) / 10,
      width: Math.round(zoneWidth * 10) / 10,
      height: Math.round(zoneHeight * 10) / 10,
    });
  });

  return results;
}

export function calculateAutoLayoutForRacks(
  racks: WarehouseRack[],
  zones: WarehouseZone[],
  config: Partial<AutoLayoutConfig> = {}
): RackLayoutResult[] {
  const cfg = { ...DEFAULT_AUTO_LAYOUT_CONFIG, ...config };
  const results: RackLayoutResult[] = [];

  if (racks.length === 0) return results;

  const racksByZone = groupRacksByZone(racks, zones);
  const zoneBoxes = new Map(zones.map(z => [z.id, getZoneBoundingBox(z)]));

  for (const zone of zones) {
    const zoneRacks = racksByZone.get(zone.id) || [];
    if (zoneRacks.length === 0) continue;

    const zoneBox = zoneBoxes.get(zone.id)!;
    const innerPadding = 1;

    const availableWidth = zoneBox.width - innerPadding * 2;
    const availableHeight = zoneBox.height - innerPadding * 2;

    const sortedRacks = sortRacksByNumber(zoneRacks);

    const totalRackWidth = sortedRacks.reduce((sum, r) => sum + r.rack_length, 0);
    const totalRackHeight = sortedRacks.reduce((max, r) => Math.max(max, r.rack_depth), 0);

    const useHorizontalLayout = totalRackWidth <= availableWidth;

    if (useHorizontalLayout) {
      let currentX = zoneBox.x + innerPadding;
      const y = zoneBox.y + innerPadding;

      for (const rack of sortedRacks) {
        results.push({
          rackId: rack.id,
          x: Math.round(currentX * 10) / 10,
          y: Math.round(y * 10) / 10,
          rotation: 0,
        });
        currentX += rack.rack_length + cfg.rackSpacing;
      }
    } else {
      const cols = Math.ceil(Math.sqrt(sortedRacks.length));
      const rows = Math.ceil(sortedRacks.length / cols);

      const cellWidth = (availableWidth - (cols - 1) * cfg.rackSpacing) / cols;
      const cellHeight = (availableHeight - (rows - 1) * cfg.rackSpacing) / rows;

      sortedRacks.forEach((rack, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);

        const x = zoneBox.x + innerPadding + col * (cellWidth + cfg.rackSpacing);
        const y = zoneBox.y + innerPadding + row * (cellHeight + cfg.rackSpacing);

        results.push({
          rackId: rack.id,
          x: Math.round(x * 10) / 10,
          y: Math.round(y * 10) / 10,
          rotation: 0,
        });
      });
    }
  }

  const unassignedRacks = racksByZone.get(null) || [];
  if (unassignedRacks.length > 0) {
    let maxZoneY = cfg.padding;
    for (const zone of zones) {
      const box = zoneBoxes.get(zone.id)!;
      maxZoneY = Math.max(maxZoneY, box.y + box.height);
    }

    const startY = zones.length > 0 ? maxZoneY + cfg.zoneSpacing : cfg.padding;
    let currentX = cfg.padding;
    let currentY = startY;
    let rowHeight = 0;

    const sortedUnassigned = sortRacksByNumber(unassignedRacks);
    const availableWidth = cfg.warehouseLength - cfg.padding * 2;

    for (const rack of sortedUnassigned) {
      if (currentX + rack.rack_length > cfg.padding + availableWidth) {
        currentX = cfg.padding;
        currentY += rowHeight + cfg.rackSpacing;
        rowHeight = 0;
      }

      results.push({
        rackId: rack.id,
        x: Math.round(currentX * 10) / 10,
        y: Math.round(currentY * 10) / 10,
        rotation: 0,
      });

      currentX += rack.rack_length + cfg.rackSpacing;
      rowHeight = Math.max(rowHeight, rack.rack_depth);
    }
  }

  return results;
}

export function calculateOptimalZoneSize(
  rackCount: number,
  avgRackLength: number,
  avgRackDepth: number,
  config: Partial<AutoLayoutConfig> = {}
): { width: number; height: number } {
  const cfg = { ...DEFAULT_AUTO_LAYOUT_CONFIG, ...config };

  const cols = Math.ceil(Math.sqrt(rackCount));
  const rows = Math.ceil(rackCount / cols);

  const width = cols * avgRackLength + (cols - 1) * cfg.rackSpacing + cfg.padding * 2;
  const height = rows * avgRackDepth + (rows - 1) * cfg.rackSpacing + cfg.padding * 2;

  return {
    width: Math.round(width * 10) / 10,
    height: Math.round(height * 10) / 10,
  };
}

export function distributeItemsEvenly(
  count: number,
  availableSpace: number,
  itemSize: number,
  minSpacing: number
): number[] {
  if (count === 0) return [];
  if (count === 1) return [availableSpace / 2 - itemSize / 2];

  const totalItemSize = count * itemSize;
  const totalSpacing = availableSpace - totalItemSize;
  const spacing = Math.max(minSpacing, totalSpacing / (count + 1));

  const positions: number[] = [];
  for (let i = 0; i < count; i++) {
    positions.push(spacing + i * (itemSize + spacing));
  }

  return positions;
}
